import { apiClient, idempotentConfig, unwrap } from './client';
import type {
  ApiEnvelope,
  ThemeAssetUploadedView,
  ThemeRequest,
  ThemeRevertResult,
  ThemeVersionView,
  ThemeView,
} from './types';

/**
 * White-label theming (OPSFL-CLIENT-PLATFORM P1 slice 3 — the /v1/theme* staff plane; keys
 * theme.read / theme.write). The ledger flow is draft → publish → (revert): a draft is a new
 * version row, publish freezes it (content hash, brd-0003 trigger) and makes it the ONE live
 * theme, revert re-activates a frozen published version or (bare) un-themes the hub. Every
 * write is validated against CLOSED sets server-side — the G23 sandbox for custom CSS, the
 * 20-field Palette for tokens, the fixed brand key set — and this module never widens them.
 */

/** The authorable closed sets, mirrored VERBATIM from the server for form affordances only —
 *  the server remains the enforcer (HubThemeService / ThemeCssSanitiser). */
export const THEME_LAYOUTS = ['CLASSIC', 'COMPACT', 'SPLIT'] as const;
export const THEME_ASSET_KINDS = ['MARK', 'WORDMARK', 'FAVICON', 'EMAIL_MARK', 'FONT'] as const;
export const THEME_ASSET_MEDIA_TYPES = ['image/png', 'image/svg+xml', 'image/jpeg'] as const;
export const THEME_ASSET_MAX_BYTES = 1024 * 1024;
export const PALETTE_FIELDS = [
  'bgBase',
  'bgContainer',
  'bgElevated',
  'bgSubtle',
  'borderStrong',
  'border',
  'borderSubtle',
  'textPrimary',
  'textSecondary',
  'textTertiary',
  'textInverse',
  'primary',
  'primaryHover',
  'primaryActive',
  'focusRing',
  'positive',
  'attention',
  'negative',
  'info',
  'processing',
] as const;

/**
 * GET /v1/theme — the live theme. `published: []` = the hub is un-themed (the data room and
 * branded email use their built-in platform identity — an honest state, not an error).
 */
export async function getPublishedTheme(): Promise<ThemeView | null> {
  const { data } = await apiClient.get<ApiEnvelope<{ published?: ThemeView[] }>>('/v1/theme');
  const published = unwrap(data)?.published;
  return Array.isArray(published) && published.length ? published[0] : null;
}

/** GET /v1/theme/versions — the full ledger, newest first. */
export async function listThemeVersions(): Promise<ThemeVersionView[]> {
  const { data } = await apiClient.get<ApiEnvelope<ThemeVersionView[]>>('/v1/theme/versions');
  const body = unwrap(data);
  return Array.isArray(body) ? body : [];
}

/** POST /v1/theme — a new DRAFT version (201). 422s carry the precise closed-set refusal. */
export async function draftTheme(req: ThemeRequest, idemKey?: string): Promise<ThemeView> {
  const { data } = await apiClient.post<ApiEnvelope<ThemeView>>(
    '/v1/theme',
    req,
    idempotentConfig(idemKey),
  );
  return unwrap(data);
}

/** POST /v1/theme/publish — freeze + go live (idempotent on an already-published version). */
export async function publishTheme(version: number, idemKey?: string): Promise<ThemeView> {
  const { data } = await apiClient.post<ApiEnvelope<ThemeView>>(
    '/v1/theme/publish',
    { version },
    idempotentConfig(idemKey),
  );
  return unwrap(data);
}

/**
 * POST /v1/theme/revert — `toVersion` re-activates a frozen PUBLISHED version (what you revert to
 * is exactly what was live); no body un-themes the hub entirely.
 */
export async function revertTheme(
  toVersion: number | null,
  idemKey?: string,
): Promise<ThemeRevertResult> {
  const { data } = await apiClient.post<ApiEnvelope<ThemeRevertResult>>(
    '/v1/theme/revert',
    toVersion == null ? {} : { toVersion },
    idempotentConfig(idemKey),
  );
  return unwrap(data);
}

/** POST /v1/theme/assets — multipart upload (kind + file; ≤1 MiB; png/svg/jpeg; SVGs sanitised). */
export async function uploadThemeAsset(
  kind: string,
  file: File,
  idemKey?: string,
): Promise<ThemeAssetUploadedView> {
  const form = new FormData();
  form.append('kind', kind);
  form.append('file', file);
  const { data } = await apiClient.post<ApiEnvelope<ThemeAssetUploadedView>>(
    '/v1/theme/assets',
    form,
    idempotentConfig(idemKey),
  );
  return unwrap(data);
}

export interface ThemeVocabulary {
  radii: string[];
  fontMediaTypes: string[];
  maxFontBytes: number;
  maxFonts: number;
}

/** GET /v1/theme/vocabulary — the closed design lists (OL-23). */
export async function getThemeVocabulary(): Promise<ThemeVocabulary> {
  const { data } = await apiClient.get<ApiEnvelope<ThemeVocabulary>>('/v1/theme/vocabulary');
  return unwrap(data);
}

const FONT_TYPE_BY_EXTENSION: Record<string, string> = {
  woff2: 'font/woff2',
  woff: 'font/woff',
  ttf: 'font/ttf',
  otf: 'font/otf',
};

/**
 * A font file with the type its extension claims. Browsers often label fonts blank or
 * `application/octet-stream`; the server holds this claim to the file's bytes either way.
 */
export function asFontFile(file: File): File {
  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
  return new File([file], file.name, { type: FONT_TYPE_BY_EXTENSION[extension] ?? file.type });
}

/** GET /v1/theme/assets/{id} as bytes: an image or font for the studio's preview. */
export async function readThemeAsset(assetId: string): Promise<{ bytes: ArrayBuffer; mediaType: string }> {
  const { data, headers } = await apiClient.get<ArrayBuffer>(
    `/v1/theme/assets/${encodeURIComponent(assetId)}`,
    { responseType: 'arraybuffer' },
  );
  return { bytes: data, mediaType: String(headers['content-type'] ?? '') };
}

/** The (bearer-authenticated) asset read URL — for reference display, not <img> hotlinking. */
export function themeAssetPath(assetId: string): string {
  return `/api/v1/theme/assets/${encodeURIComponent(assetId)}`;
}
